import {
  QAlert,
  QButton,
  QLink,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
} from '@qualio/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SuppressedAssignedTraineesToDocuments } from '../../../../../../../../api/document';
import { MedtechUserV2 } from '../../../../../../../../api/user';

type SuppressedAssignmentsModalProps = {
  suppressedAssignments?: SuppressedAssignedTraineesToDocuments[];
  onClose: () => void;
};

const getElement = (
  code: string,
  documentId: number,
  suppressedAssignments?: Pick<MedtechUserV2, 'email' | 'full_name' | 'id'>[],
) =>
  suppressedAssignments?.map((trainee) => (
    <QStack key={trainee.id} direction="row">
      <QLink
        href={`/workspace/documents/${documentId}`}
        isExternal
        tabIndex={-1}
        marginBottom={1}
        data-cy={`trainee-already-assigned-${documentId}`}
      >
        {code}
      </QLink>
      <QText>-</QText>
      <QText>{trainee.full_name}</QText>
    </QStack>
  ));

export const SuppressedAssignmentsModal = ({
  suppressedAssignments,
  onClose,
}: SuppressedAssignmentsModalProps) => {
  const { t } = useTranslation();
  const suppressedDescriptions = useMemo(
    () =>
      suppressedAssignments?.map((assignment) => {
        const alreadyAssigned = getElement(
          assignment.code,
          assignment.id,
          assignment?.suppressed?.current_trainee,
        );
        return [...(alreadyAssigned ?? [])];
      }),
    [suppressedAssignments],
  );

  return (
    <QModal
      onClose={onClose}
      isOpen={!!suppressedAssignments}
      data-cy="suppressed-training-assignment-modal"
      size="xl"
    >
      <QModalHeader>
        <QText>{t('buttons.assignTrainees')}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing={6}>
          <QAlert
            title={t('messages.someUsersUnableToBeAssignedTraining')}
            description={t('messages.usersAlreadyHaveTrainingAssigned')}
            status="warning"
          />
          <QText>
            {t('messages.someUsersUnableToBeAssignedTraining').replace(
              '.',
              ':',
            )}
          </QText>
          <QStack spacing={3}>{suppressedDescriptions}</QStack>
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton data-cy="close-suppressed-assignments-modal" onClick={onClose}>
          {t('buttons.close')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
